import React from 'react';
import { Popover } from 'antd';

export type SendMode = 'Enter' | 'Shift+Enter' | 'Ctrl+Enter' | 'Alt+Enter';

export const SEND_MODE_OPTIONS: SendMode[] = ['Enter', 'Shift+Enter', 'Ctrl+Enter', 'Alt+Enter'];

export const SEND_MODE_PLACEHOLDER: Record<SendMode, string> = {
  Enter: '按下 "Enter" 发送消息',
  'Shift+Enter': '按下 "Shift+Enter" 发送消息',
  'Ctrl+Enter': '按下 "Ctrl+Enter" 发送消息',
  'Alt+Enter': '按下 "Alt+Enter" 发送消息'
};

export const SEND_MODE_KEY = 'ai_floating_send_mode';

export const readSendMode = (): SendMode => (localStorage.getItem(SEND_MODE_KEY) as SendMode | null) || 'Enter';

export const saveSendMode = (mode: SendMode) => localStorage.setItem(SEND_MODE_KEY, mode);

/** 键盘事件是否命中当前发送快捷键（Ctrl 同时兼容 Mac 的 Command 键） */
export const isSendKey = (e: React.KeyboardEvent, sendMode: SendMode) => {
  if (e.key !== 'Enter') return false;
  const shift = e.shiftKey;
  const ctrl = e.ctrlKey || e.metaKey;
  const alt = e.altKey;
  return (
    (sendMode === 'Enter' && !shift && !ctrl && !alt) ||
    (sendMode === 'Shift+Enter' && shift && !ctrl && !alt) ||
    (sendMode === 'Ctrl+Enter' && ctrl && !shift && !alt) ||
    (sendMode === 'Alt+Enter' && alt && !shift && !ctrl)
  );
};

/** 快捷键菜单内容：与浮窗抽屉样式一致 */
function SendModeMenu({ sendMode, onSelect }: { sendMode: SendMode; onSelect: (mode: SendMode) => void }) {
  return (
    <div style={{ padding: '6px 0', minWidth: 130 }}>
      {SEND_MODE_OPTIONS.map((mode) => (
        <div
          key={mode}
          onClick={() => onSelect(mode)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 14px',
            fontSize: 13,
            cursor: 'pointer',
            color: mode === sendMode ? '#1677ff' : '#1d2129',
            fontWeight: mode === sendMode ? 600 : 400,
            transition: 'background 0.1s'
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <span>{mode}</span>
          {mode === sendMode && (
            <svg viewBox="0 0 12 12" fill="none" width={12} height={12}>
              <path d="M2 6l3 3 5-5" stroke="#1677ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
      ))}
    </div>
  );
}

/** 发送快捷键选择器：按钮 + antd Popover 弹出菜单，样式与浮窗抽屉一致 */
export function SendModeSelector({
  sendMode,
  onChange,
  open,
  onOpenChange
}: {
  sendMode: SendMode;
  onChange: (mode: SendMode) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      trigger="click"
      placement="topLeft"
      arrow={false}
      destroyTooltipOnHide
      zIndex={1201}
      overlayInnerStyle={{ padding: 0, borderRadius: 10, overflow: 'hidden' }}
      content={
        <SendModeMenu
          sendMode={sendMode}
          onSelect={(mode) => {
            onChange(mode);
            saveSendMode(mode);
            onOpenChange(false);
          }}
        />
      }
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          padding: '0 8px',
          height: 26,
          borderRadius: 6,
          background: open ? '#e8f0fe' : '#f2f3f5',
          color: open ? '#1677ff' : '#4e5969',
          fontSize: 12,
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'all 0.15s',
          whiteSpace: 'nowrap'
        }}
        onMouseEnter={(e) => {
          if (!open) {
            e.currentTarget.style.background = '#e8f0fe';
            e.currentTarget.style.color = '#1677ff';
          }
        }}
        onMouseLeave={(e) => {
          if (!open) {
            e.currentTarget.style.background = '#f2f3f5';
            e.currentTarget.style.color = '#4e5969';
          }
        }}
      >
        <span>{sendMode}</span>
        <svg
          viewBox="0 0 10 6"
          fill="none"
          width={8}
          height={8}
          style={{
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.15s'
          }}
        >
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </Popover>
  );
}
